import React, { useState } from 'react';
import Button from '../../components/ui/Button';
import { useNavigation } from 'react-router-dom';
import { SquarePen, Trash2 } from 'lucide-react';
import ModifyGame from '../../components/Popup/ModifyGame';
// import { getImageByCode } from "../../utils/getGameImage";
import { getGameImage } from '../../utils/getGameImage';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

export default function ManageGamesCard({
  id,
  image,
  name,
  active,
  bet_limit,
  commissionRate,
  description,
  doubleWin,
  tripleWin,
  winRate,
  game_settings_id,
  allGameSettings,
  user_id,
  refreshGames,
  isPublic = false,
  // isActive,
  onToggle,
  onModify,
  // onDelete,
}) {
  const { tokens, user } = useAuth();
  const [isGamePublic, setIsGamePublic] = useState(isPublic);
  const isAdmin = user && user.roleId === 1;

  const toggleHandler = async () => {
    if (onToggle) {
      // Use the passed onToggle function from parent component
      onToggle(id, active);
    } else {
      // Fallback to internal implementation
      try {
        const newActiveState = !active;
        const response = await gamesApi.updateGame(tokens.access.token, id, {
          active: newActiveState,
          name,
          commissionRate: parseInt(commissionRate),
          winRate: parseInt(winRate),
          doubleWin: parseInt(doubleWin),
          tripleWin: parseInt(tripleWin),
          gameSettingsId: game_settings_id,
          userId: user_id,
        });

        if (response.status === 200) {
          toast.success(
            `Game ${newActiveState ? 'activated' : 'deactivated'} successfully`
          );
        } else {
          toast.error('Failed to update game status');
        }
      } catch (error) {
        toast.error('Error updating game status');
        console.error('Error updating game status:', error);
      }
    }
  };

  const togglePublicHandler = async () => {
    try {
      const newPublicState = !isGamePublic;
      const response = await gamesApi.updateGame(tokens.access.token, id, {
        isPublic: newPublicState,
        name,
        commissionRate: parseInt(commissionRate),
        winRate: parseInt(winRate),
        doubleWin: parseInt(doubleWin),
        tripleWin: parseInt(tripleWin),
        gameSettingsId: game_settings_id,
        userId: user_id,
        active: active,
      });

      if (response.status === 200) {
        setIsGamePublic(newPublicState);
        toast.success(
          `Game ${newPublicState ? 'made public' : 'made private'} successfully`
        );
      } else {
        toast.error('Failed to update game visibility');
      }
    } catch (error) {
      toast.error('Error updating game visibility');
      console.error('Error updating game visibility:', error);
    }
  };

  const handleSave = async (gameData) => {
    setIsGamePublic(gameData.isPublic);
    try {
      const response = await gamesApi.updateGame(tokens.access.token, id, {
        name: gameData.name,
        commissionRate: parseInt(gameData.commissionRate),
        winRate: parseInt(gameData.winRate),
        doubleWin: parseInt(gameData.doubleWin),
        tripleWin: parseInt(gameData.tripleWin),
        gameSettingsId: gameData.gameSettingsId,
        userId: user_id,
        isPublic: isGamePublic,
      });

      if (response.status === 200) {
        toast.success('Game updated successfully');
        refreshGames();
      } else {
        toast.error('Failed to update game');
      }
    } catch (error) {
      toast.error('Error updating game');
      console.error('Error updating game:', error);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      cancelButtonColor: '#d33',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await gamesApi.deleteGame(tokens.access.token, id);
          if (response.status === 204) {
            Swal.fire({
              title: 'Success',
              text: 'Game deleted successfully',
              icon: 'success',
            });
            refreshGames();
          } else {
            Swal.fire({
              title: 'Error',
              text: 'Failed to delete game',
              icon: 'error',
            });
          }
        } catch (error) {
          toast.error('Error deleting game');
          console.error('Error deleting game:', error);
        }
      }
    });
  };

  const GameImage = getGameImage(image.image);

  return (
    <>
      <div className='flex items-start gap-2.5 border p-2.5 mt-2.5 rounded-md'>
        <img
          src={GameImage}
          alt={image?.name}
          className='rounded-lg'
          style={{ width: '62px', height: '62px' }}
        />
        <div className='flex-1'>
          <div className='flex items-center justify-between'>
            <h3 className='font-bold text-base'>{name}</h3>

            <div className='flex items-center gap-2'>
              {isAdmin && (
                <button
                  onClick={togglePublicHandler}
                  className={`flex items-center rounded-full px-2 w-20 h-6 transition-colors duration-300 ${
                    isGamePublic ? 'bg-blue-500' : 'bg-gray-100'
                  }`}
                  style={{ minWidth: 80, height: 24 }}
                >
                  {isGamePublic ? (
                    <>
                      <span className='text-[10px] font-normal text-white'>
                        Public
                      </span>
                      <div className='w-4 h-4 rounded-full bg-white shadow-md ml-1'></div>
                    </>
                  ) : (
                    <>
                      <div className='w-4 h-4 rounded-full bg-white shadow-md mr-1'></div>
                      <span className='text-[10px] font-normal text-gray-800'>
                        Private
                      </span>
                    </>
                  )}
                </button>
              )}
              <button
                onClick={toggleHandler}
                className={`flex items-center rounded-full px-2 w-20 h-6 transition-colors duration-300 ${
                  active ? 'bg-green-500' : 'bg-gray-300'
                }`}
                style={{ minWidth: 80, height: 24 }}
              >
                {active ? (
                  <>
                    <span className='text-[10px] font-normal text-white'>
                      Active
                    </span>
                    <div className='w-4 h-4 rounded-full bg-white shadow-md ml-1'></div>
                  </>
                ) : (
                  <>
                    <div className='w-4 h-4 rounded-full bg-white shadow-md mr-1'></div>
                    <span className='text-[10px] font-normal text-gray-800'>
                      Inactive
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className='flex items-center gap-2 mt-5'>
            <span className='text-xs text-[#12131A] font-normal'>
              Game Reference: <span className='text-[#0E64D1]'>{name}</span>{' '}
            </span>
          </div>
          <div className='flex items-center gap-2 '>
            <span className='text-xs text-[#12131A] font-normal'>
              Game Rates: <span className='text-[#0E64D1]'>₱{winRate}</span>
            </span>
          </div>

          <div className='grid grid-cols-3 md:grid-cols-4 gap-4 mt-4'>
            <div>
              <div className='text-[10px] text-[#65758B]'>
                {image.image === '4D' || image.image === '6D'
                  ? '1st Prize'
                  : 'Win'}
              </div>
              <div className='font-semibold text-xs'>₱{winRate}</div>
            </div>

            <div>
              <div className='text-[10px] text-[#65758B]'>Commission</div>
              <div className='font-semibold text-xs'>{commissionRate}%</div>
            </div>

            <div>
              <div className='text-[10px] text-[#65758B]'>
                {image.image === '4D' || image.image === '6D'
                  ? '2nd Prize'
                  : 'Double Win'}
              </div>
              <div className='font-semibold text-xs'>₱{doubleWin}</div>
            </div>

            {/* Triple Win: full row on small screens, 4th col on md+ */}
            <div className='col-span-3 md:col-span-1'>
              <div className='text-[10px] text-[#65758B]'>
                {image.image === '4D' || image.image === '6D'
                  ? '3rd Prize'
                  : 'Triple Win'}
              </div>
              <div className='font-semibold text-xs'>₱{tripleWin}</div>
            </div>
          </div>

          <div className='flex text-xs font-normal mt-2.5 gap-2.5'>
            {isAdmin && (
              <>
                <button
                  className='flex-1 bg-[#0E64D1] text-white text-xs rounded-[4px]  flex justify-center items-center gap-2 py-2.5'
                  onClick={() =>
                    onModify({
                      id,
                      name,
                      commissionRate,
                      winRate,
                      doubleWin,
                      tripleWin,
                      game_settings_id,
                      user_id,
                      public: isGamePublic,
                    })
                  }
                >
                  <SquarePen width={14} height={14} />
                  Modify
                </button>

                <button
                  className='flex-1 bg-red-600 text-white text-xs rounded-[4px] flex justify-center items-center gap-2  py-2.5'
                  onClick={() => handleDelete(id)}
                >
                  <Trash2 width={14} height={14} />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
